import { T } from "../themes";
import { TODAY_ISO, MEALS, MEAL_ICON, clamp, fmtDate } from "../constants";
import { useChatCap } from "../llm";
import React, { useState, useEffect, useRef } from "react";
import { Brain, ArrowClockwise, Flame, ChartBar, CaretDown, PaperPlaneTilt, WarningCircle } from "@phosphor-icons/react";
import Spinner from "../components/Spinner";
import BottomNav from "../components/BottomNav";
import BurnDownChart from "../components/BurnDownChart";
import AppHeader from "../components/AppHeader";
import FeatureHint from "../components/FeatureHint";
import AIText from "../components/AIText";

// ── Reusable Chat Panel component ──────────────────────────────────────────
function ChatPanel({ title, scopeNote, placeholder, cap, history, loading, error, onSend }) {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const msgsRef = useRef(null);
  useEffect(() => { if (msgsRef.current) msgsRef.current.scrollTop = msgsRef.current.scrollHeight; }, [history, loading]);
  const pillColor = cap.remaining <= 3 ? T.red : T.aiColor;
  const pillText = cap.remaining === 0 ? "No messages left" : `${cap.remaining} messages left`;
  const canSend = !cap.isExhausted && !loading && input.trim();
  const handleSend = () => {
    if (!canSend) return;
    onSend(input.trim());
    setInput("");
  };
  return (
    <div style={{ background: T.card, border: `1px solid ${T.cardBorder}`, borderRadius: 18, marginBottom: 8, overflow: "hidden" }}>
      {/* Toggle row */}
      <button onClick={() => setOpen(o => !o)} style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "12px 14px", minHeight: 44, backgroundColor: T.tileBg, border: "none", cursor: "pointer",
        borderBottom: open ? "1px solid rgba(255,255,255,0.08)" : "none",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 14 }}>💬</span>
          <span style={{ color: T.tileAccent, fontSize: 11, fontWeight: 700, fontFamily: T.fontSans, letterSpacing: "0.5px" }}>{title}</span>
          <span style={{ background: "rgba(255,255,255,0.1)", color: "rgba(255,255,255,0.6)", fontSize: 10, fontWeight: 700, borderRadius: 8, padding: "3px 8px", fontFamily: T.fontSans }}>{pillText}</span>
        </div>
        <CaretDown size={14} color={T.tileAccent} style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }} />
      </button>
      {/* Expanded body */}
      {open && (
        <div style={{ borderTop: T.border }}>
          {/* Messages */}
          <div ref={msgsRef} style={{ padding: "12px 14px 8px", display: "flex", flexDirection: "column", gap: 8, maxHeight: 300, overflowY: "auto" }}>
            {history.length === 0 && !loading && !error && (
              <p style={{ color: T.textDim, fontSize: 11, fontFamily: T.fontSans, textAlign: "center", margin: "8px 0" }}>Ask a question to get started.</p>
            )}
            {history.map((msg, i) => (
              <div key={i} style={{
                alignSelf: msg.role === "user" ? "flex-end" : "flex-start",
                maxWidth: "85%",
                background: msg.role === "user" ? T.aiColor : T.surface,
                border: msg.role === "user" ? "none" : T.border,
                color: msg.role === "user" ? "#fff" : T.ink,
                borderRadius: msg.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px",
                padding: "9px 12px", fontSize: 11, lineHeight: 1.5, fontFamily: T.fontSans,
              }}>
                {msg.role === "assistant" ? <AIText text={msg.content} /> : msg.content}
              </div>
            ))}
            {loading && (
              <div style={{ alignSelf: "flex-start", maxWidth: "85%", background: T.surface, border: T.border, borderRadius: "12px 12px 12px 4px", padding: "9px 12px" }}>
                <span style={{ color: T.textDim, fontSize: 11, fontFamily: T.fontSans }}>Thinking...</span>
              </div>
            )}
            {error && (
              <div style={{ alignSelf: "flex-start", maxWidth: "85%", background: T.surface, border: T.border, borderRadius: "12px 12px 12px 4px", padding: "9px 12px" }}>
                <span style={{ color: T.red || "#e24b4a", fontSize: 11, fontFamily: T.fontSans }}>{String(error)}</span>
              </div>
            )}
            {cap.isExhausted && (
              <div style={{ background: T.card, border: T.border, borderRadius: 12, padding: "10px 14px", textAlign: "center" }}>
                <span style={{ color: T.textSub, fontSize: 11, fontWeight: 700, fontFamily: T.fontSans }}>No messages left today. Resets at midnight.</span>
              </div>
            )}
          </div>
          {/* Scope note */}
          <p style={{ fontSize: 10, color: T.textDim, textAlign: "center", padding: "0 14px 8px", margin: 0, fontFamily: T.fontSans }}>{scopeNote}</p>
          {/* Input row */}
          <div style={{ borderTop: T.border, background: T.card, padding: "8px 14px 12px", display: "flex", gap: 8 }}>
            <textarea
              value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
              placeholder={cap.isExhausted ? "Resets at midnight" : placeholder}
              disabled={cap.isExhausted}
              rows={1}
              style={{
                flex: 1, padding: "9px 12px", borderRadius: T.radiusSm, border: T.border,
                fontSize: 11, background: T.surface, color: T.ink, outline: "none", minHeight: 44,
                fontFamily: T.fontSans, resize: "none",
                opacity: cap.isExhausted ? 0.5 : 1,
              }}
            />
            <button onClick={handleSend} disabled={!canSend} style={{
              width: 44, height: 44, borderRadius: T.radiusSm, border: "none", cursor: canSend ? "pointer" : "default",
              background: cap.isExhausted ? T.textDim : T.aiColor,
              opacity: canSend ? 1 : 0.4,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <PaperPlaneTilt size={16} color="#fff" weight="fill" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default function IntelligenceScreen({
  shell,
  phone,
  OfflineBanner,
  screen,
  setScreen,
  session,
  hasTargets,
  weight,
  waterMl,
  targets,
  totals,
  persona,
  personaData,
  darkMode, setDarkMode,
  logHistory,
  intelByDay,
  intelLoading,
  loadIntelMeta,
  intelView,
  setIntelView,
  cpLoading,
  cpHeadline,
  cpMetabolic,
  cpTactical,
  cpOutlook,
  cpAction,
  onRunCopilot,
  weeklyLoading,
  weeklyTs,
  wbSummary,
  wbPros,
  wbNegatives,
  wbPriorities,
  onRunWeeklyBriefing,
  habitMetrics,
  setHabitMetrics,
  autoAuditTime,
  hydrationTarget,
  logs,
  crossAppData,
  crossAppLoading,
  onRunCrossApp,
  hints,
  dayHistory, setDayHistory, saveProfile,
  progressMacros, setProgressMacros, showProgressFilter, setShowProgressFilter,
  MEAL_COLOR, mealBreakdown,
  buildDailyAuditChatContext, buildWeeklyChatContext,
  sendChatMessage, getLLMUrl,
  userId,
  effectiveIsPremium,
}) {
  // V28: Show daily audit hint on first Intelligence screen visit — only when user has logged food
  useEffect(() => {
    if (logs && logs.length > 0) hints.showHint("daily_audit");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Chat state — daily ──────────────────────────────────────────────────────
  const dailyChatCap = useChatCap(`nutri_chat_daily_${userId}`, 15);
  const [dailyChatHistory, setDailyChatHistory] = useState([]);
  const [dailyChatLoading, setDailyChatLoading] = useState(false);
  const [dailyChatError, setDailyChatError] = useState(null);
  async function handleDailyChatSend(userMessage) {
    if (!effectiveIsPremium || dailyChatCap.isExhausted || !userMessage) return;
    setDailyChatLoading(true); setDailyChatError(null);
    try {
      const systemPrompt = buildDailyAuditChatContext();
      const reply = await sendChatMessage({ systemPrompt, history: dailyChatHistory, userMessage, getLLMUrl });
      setDailyChatHistory(prev => [...prev, { role: "user", content: userMessage }, { role: "assistant", content: reply }]);
      dailyChatCap.increment();
    } catch (err) { setDailyChatError(err); }
    finally { setDailyChatLoading(false); }
  }

  // ── Chat state — weekly ─────────────────────────────────────────────────────
  const weeklyChatCap = useChatCap(`nutri_chat_weekly_${userId}`, 10);
  const [weeklyChatHistory, setWeeklyChatHistory] = useState([]);
  const [weeklyChatLoading, setWeeklyChatLoading] = useState(false);
  const [weeklyChatError, setWeeklyChatError] = useState(null);
  async function handleWeeklyChatSend(userMessage) {
    if (!effectiveIsPremium || weeklyChatCap.isExhausted || !userMessage) return;
    setWeeklyChatLoading(true); setWeeklyChatError(null);
    try {
      const systemPrompt = buildWeeklyChatContext();
      const reply = await sendChatMessage({ systemPrompt, history: weeklyChatHistory, userMessage, getLLMUrl });
      setWeeklyChatHistory(prev => [...prev, { role: "user", content: userMessage }, { role: "assistant", content: reply }]);
      weeklyChatCap.increment();
    } catch (err) { setWeeklyChatError(err); }
    finally { setWeeklyChatLoading(false); }
  }

  // ── Charts 7-day log filter state ─────────────────────────────────────────
  const LOG_METRICS = ["Calories","Protein","Carbs","Fat","Fibre","Water","Sugar","Sodium"];
  const [logMetrics, setLogMetrics] = useState(() => {
    try {
      const saved = localStorage.getItem('lithe_log_metrics');
      return saved ? JSON.parse(saved) : ["Calories","Protein","Carbs","Fat","Fibre"];
    } catch { return ["Calories","Protein","Carbs","Fat","Fibre"]; }
  });
  const toggleLogMetric = (metric) => {
    setLogMetrics(prev => {
      const next = prev.includes(metric) ? prev.filter(m => m !== metric) : [...prev, metric];
      const result = next.length === 0 ? [metric] : next;
      try { localStorage.setItem('lithe_log_metrics', JSON.stringify(result)); } catch {}
      return result;
    });
  };

  // Null-safe fallback — prevents crash if personaData prop is undefined
  const pd = personaData || { color: T.accent, emoji: "⚖️" };
  const auditRef = useRef(null);
  const chatRef  = useRef(null);
  const mealsRef = useRef(null);

  // ── Local computations (were at the top of the if-block in App.js) ──

  // Use logHistory (logs table) for accurate 7-day data — same source as Progress tab
  const last7iso = Array.from({length:7},(_,i)=>{
    const d=new Date(); d.setDate(d.getDate()-(6-i));
    return d.toISOString().slice(0,10);
  });
  const lhByDate = {};
  (logHistory||[]).forEach(d=>{ lhByDate[d.date]=d; });
  // r7 needs .calories, .target, .logged — shape matches logHistory
  const r7 = last7iso.map(date=>({
    date,
    calories: lhByDate[date]?.calories||0,
    protein:  lhByDate[date]?.protein||0,
    carbs:    lhByDate[date]?.carbs||0,
    fat:      lhByDate[date]?.fat||0,
    target:   targets.calories,
    logged:   lhByDate[date]?.logged||false,
  }));

  // ── Module 2: Metabolic Momentum ──────────────────────────────────────
  const daysLogged = r7.filter(d=>d.calories>0).length;
  const daysMet    = r7.filter(d=>d.calories>0 && d.calories<=(d.target||targets.calories||0)).length;
  const momentum   = Math.round((daysMet / 7) * 100);
  const momentumColor = "#F5A623"; // Fixed amber — not persona-dependent
  const momentumVerdict = (() => {
    if (daysLogged === 0) return "Zero data. Zero progress. Log something.";
    if (daysLogged === 1) return "One day is not a trend. It is a fluke. Log your food or stop pretending.";
    if (daysLogged < 4)  return `${daysLogged} days out of 7. Unreliable data set. Stop hiding.`;
    if (momentum >= 85)  return `${daysMet}/7 days on target. Adequate. Do not confuse adequate with good.`;
    if (momentum >= 60)  return `${daysMet}/7 days on target. You are getting there. Or you are lying to yourself. Check the numbers.`;
    if (momentum >= 40)  return `${daysMet}/7 days on target. More days off than on. This is not a diet. This is a schedule conflict.`;
    return `${daysMet}/7 days on target. Metabolic stall is imminent. You know what the problem is.`;
  })();

  // Wall of Shame — removed in V41

  // ── Module 4: Survival Probability ───────────────────────────────────
  const consistencyScore = Math.round((daysLogged / 7) * 40);
  const complianceScore  = Math.round((daysMet    / 7) * 40);
  const hydrationScore   = 10;
  let trendAdj = 0;
  if (r7.length >= 3) {
    const first3avg = r7.slice(0,3).reduce((s,d)=>s+d.calories,0)/3;
    const last3avg  = r7.slice(-3).reduce((s,d)=>s+d.calories,0)/3;
    if (last3avg - first3avg >  150) trendAdj = -8;
    if (first3avg - last3avg >  100) trendAdj =  6;
  }
  const survival = clamp(consistencyScore + complianceScore + hydrationScore + trendAdj, 4, 96);
  const survivalColor = "#2ECC71"; // Fixed green — not persona-dependent
  const survivalVerdict = (() => {
    if (daysLogged === 0) return "No data. Log food to get a trajectory reading.";
    if (survival >= 80)  return "On track. Consistent compliance puts your physique goal within reach on schedule.";
    if (survival >= 65)  return "Mostly on track. A few gaps are slowing you down. Tighten them.";
    if (survival >= 45)  return "Off pace. At this rate, your goal timeline extends significantly. Fix the pattern.";
    if (survival >= 25)  return "Well off target. Compliance this low means the goal date is fiction. Rebuild.";
    return "Trajectory: zero. No consistent data, no consistent results. Start logging.";
  })();

  const needsLoad = Object.keys(intelByDay).length===0 && !intelLoading;
  const _cpFailed = (s) => typeof s==="string" && s.toLowerCase().includes("unavailable");

  return (
    <div style={shell}><div style={{...phone,background:T.pageBg,height:"100dvh",overflow:"hidden"}}>
      {OfflineBanner}

      {/* ── Header ─────────────────────────────────────────────────────── */}
      <AppHeader
        title="Intel"
        subtitle="AI insights & audit"
        persona={persona}
        darkMode={darkMode}
        onToggleDarkMode={()=>setDarkMode(d=>!d)}
        currentApp="nutri"
        onAvatarPress={()=>setScreen&&setScreen("settings")}
        username={session?.user?.email}
        rightContent={
          <button onClick={loadIntelMeta} disabled={intelLoading}
            style={{
              width:28,height:28,borderRadius:14,background:T.surfaceLight,border:"none",
              cursor:"pointer",display:"flex",alignItems:"center",justifyContent:"center",
              opacity:intelLoading?0.5:1,
            }}>
            {intelLoading?<Spinner size={10}/>:<ArrowClockwise size={14} weight="bold" color={T.inkMuted}/>}
          </button>
        }
        subContent={intelView==="daily"?(
          <div style={{padding:"6px 16px 8px",background:T.pageBg,display:"flex",gap:6,overflowX:"auto"}}>
            {[{label:"Audit",r:auditRef},{label:"Chat",r:chatRef}].map(({label,r})=>(
              <button key={label} onClick={()=>r.current?.scrollIntoView({behavior:"smooth",block:"start"})}
                style={{background:T.surfaceLight,border:`1px solid ${T.cardBorder}`,borderRadius:T.radiusPill,padding:"6px 14px",fontSize:11,fontWeight:700,fontFamily:T.fontSans,color:T.inkDark,cursor:"pointer",whiteSpace:"nowrap",flexShrink:0}}>
                {label}
              </button>
            ))}
          </div>
        ):undefined}
      />

      <div style={{display:"flex",flexDirection:"column",flex:1,minHeight:0}}>

        {/* ── View toggle — static chrome, never scrolls ─────────── */}
        <div style={{background:T.card,margin:"0 16px 12px",borderRadius:T.radiusSm,border:`1px solid ${T.cardBorder}`,padding:4,display:"flex",gap:2,flexShrink:0}}>
          {[
            { id:"daily",   label:"Daily" },
            { id:"weekly",  label:"Weekly" },
            { id:"charts",  label:"Charts" },
          ].map(({id,label})=>(
            <button key={id} onClick={()=>setIntelView(id)} style={{
              flex:1, padding:"8px 0", textAlign:"center",
              background: intelView===id ? T.primary : "transparent",
              border:"none", borderRadius:T.radiusSm,
              color: intelView===id ? "#fff" : T.inkMuted,
              cursor:"pointer", fontSize:11, fontWeight:700,
              fontFamily:T.fontSans,
            }}>
              {label}
            </button>
          ))}
        </div>

        {/* ── Single shared scroll container for all three tabs ────── */}
        <div style={{flex:1,overflowY:"auto"}}>


        {/* ============================================================
            DAILY INSIGHT VIEW
        ============================================================ */}
        {intelView==="daily"&&(
          <div style={{padding:"0 16px"}}>
            {/* ── Header block: persona + context ────────────────────── */}
            <div style={{
              background:T.tileBg,border:`1px solid ${T.tileBg}`,
              padding:"14px 16px",marginBottom:8,
              display:"flex",alignItems:"center",justifyContent:"space-between",
              borderRadius:18,
            }}>
              <div style={{display:"flex",alignItems:"center",gap:9}}>
                <div style={{width:36,height:36,borderRadius:18,backgroundColor:"rgba(255,255,255,0.12)",display:"flex",alignItems:"center",justifyContent:"center"}}>
                  <Brain size={16} color={T.tileAccent} weight="fill"/>
                </div>
                <div>
                  <p style={{color:T.tileAccent,fontSize:11,fontFamily:T.fontSans,
                    fontWeight:700,margin:0,letterSpacing:"0.5px"}}>
                    Daily micro-audit
                  </p>
                  <p style={{color:T.tileMuted,fontSize:10,fontFamily:T.fontSans,fontWeight:600,margin:0}}>
                    {weight}kg
                  </p>
                </div>
              </div>
              <div style={{background:"rgba(255,255,255,0.12)",
                padding:"3px 8px",fontSize:10,fontWeight:700,fontFamily:T.fontSans,
                color:"rgba(255,255,255,0.7)",borderRadius:6}}>
                {pd.emoji} {persona}
              </div>
            </div>

            {/* ── Macro delta row ─────────────────────────────────────── */}
            {(()=>{
              const _yIso=(()=>{const d=new Date();d.setDate(d.getDate()-1);return d.toISOString().slice(0,10);})();
              const _yData=(logHistory||[]).find(d=>d.date===_yIso)||null;
              return (
                <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,
                  display:"flex",marginBottom:12,borderRadius:T.radiusSm,overflow:"hidden"}}>
                  {[
                    {label:"Cal left", val:Math.round(targets.calories-totals.calories),     color:T.accent,  yVal:_yData?Math.round(targets.calories-(_yData.calories||0)):null},
                    {label:"Pro left", val:`${Math.round(targets.protein-totals.protein)}g`,  color:T.primary, yVal:_yData?Math.round(targets.protein-(_yData.protein||0)):null},
                    {label:"Water",    val:`${waterMl}ml`,                                    color:(()=>{if(!waterMl||waterMl===0)return T.inkMuted;const _h=new Date().getHours();const _p=waterMl/(hydrationTarget||2500);return(_h>=15&&_p<0.5)?T.red:T.inkDark;})(), yVal:_yData?(_yData.water||0):null},
                  ].map((m,i)=>{
                    const n=typeof m.val==="number"?m.val:parseInt(m.val);
                    const delta=(m.yVal!==null&&m.yVal!==undefined)?n-m.yVal:null;
                    const arrow=delta!==null?(delta>0?"↑":delta<0?"↓":"→"):null;
                    return(
                      <div key={m.label} style={{flex:1,textAlign:"center",padding:"10px 3px",
                        borderLeft:i>0?`1px solid ${T.cardBorder}`:"none"}}>
                        <p style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,margin:0,
                          letterSpacing:"1px",fontWeight:700}}>{m.label}</p>
                        <p style={{color:typeof n==="number"&&n<0?T.red:typeof n==="number"&&n===0?T.accent:T.nutriGreen,fontSize:18,
                          fontWeight:800,fontFamily:T.fontSans,margin:"3px 0 0"}}>{m.val}</p>
                        {arrow!==null&&(
                          <div style={{fontFamily:T.fontSans,fontSize:10,color:T.inkFaint,marginTop:2}}>{arrow} vs yesterday</div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })()}

            {/* ── Execute button ──────────────────────────────────────── */}
            <div ref={auditRef} style={{marginBottom:8}}>
              <button onClick={onRunCopilot} disabled={cpLoading}
                style={{
                  background:T.orange,borderRadius:T.radiusSm,padding:14,width:"100%",
                  fontFamily:T.fontDisplay,fontSize:15,fontWeight:800,color:"#fff",
                  border:"none",cursor:cpLoading?"default":"pointer",
                  display:"flex",alignItems:"center",justifyContent:"center",gap:8,
                  opacity:cpLoading?0.7:1,
                }}>
                {cpLoading
                  ?<><Spinner size={16}/> Running audit...</>
                  :<><Brain size={16} weight="fill" color="#fff"/> Run my daily audit</>}
              </button>
              {autoAuditTime && (
                <p style={{fontSize:11,color:T.inkMuted,textAlign:"center",marginTop:6,fontFamily:T.fontSans,fontWeight:500}}>
                  Auto-audit scheduled for {autoAuditTime}
                </p>
              )}
            </div>
            {hints.isVisible("daily_audit") && (
              <FeatureHint id="daily_audit" text="Your AI coach analyses your day here. Run your first audit to see how you're tracking." onDismiss={() => hints.dismissHint("daily_audit")}/>
            )}

            {/* ── Daily chat panel ──────────────────────────────────── */}
            <div ref={chatRef}>
            <ChatPanel
              title="Ask about today"
              scopeNote="Today's nutrition only · not medical advice"
              placeholder="Ask about today's nutrition..."
              cap={dailyChatCap}
              history={dailyChatHistory}
              loading={dailyChatLoading}
              error={dailyChatError}
              onSend={handleDailyChatSend}
            />
            </div>

            {/* Loading skeleton for daily audit */}
            {cpLoading&&(
              <div style={{display:"flex",flexDirection:"column",gap:8,marginBottom:8}}>
                {[1,2,3].map(i=>(
                  <div key={i} style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,padding:16}}>
                    <div style={{background:T.surfaceLight,height:10,width:"40%",marginBottom:10,borderRadius:4}}/>
                    <div style={{background:T.surfaceLight,height:8,width:"100%",marginBottom:6,borderRadius:4}}/>
                    <div style={{background:T.surfaceLight,height:8,width:"85%",marginBottom:6,borderRadius:4}}/>
                    <div style={{background:T.surfaceLight,height:8,width:"70%",borderRadius:4}}/>
                  </div>
                ))}
              </div>
            )}
            {/* ── V40: Unified audit card — headline + right_now + recovery + outlook + action ── */}
            {(cpMetabolic || cpTactical || cpOutlook) && (
              <div style={{
                background:T.card, borderRadius:18, border:`1px solid ${T.cardBorder}`,
                padding:16, marginBottom:8,
              }}>
                {/* Eyebrow + AI badge */}
                <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:12}}>
                  <span style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",fontFamily:T.fontSans}}>Daily analysis</span>
                  <span style={{background:T.aiTagBg,color:T.aiTagText,fontSize:10,fontWeight:700,borderRadius:6,padding:"3px 8px",fontFamily:T.fontSans}}>AI</span>
                </div>
                {/* Headline */}
                {cpHeadline && (
                  <p style={{color:T.primary, fontSize:16, fontWeight:700, lineHeight:1.4,
                    margin:"0 0 16px", fontFamily:T.fontSans}}>
                    <AIText text={cpHeadline} persona={persona}/>
                  </p>
                )}

                {/* RIGHT NOW */}
                {cpMetabolic && (
                  <>
                    <p style={{fontSize:10, fontWeight:700, letterSpacing:"1px", color:T.inkMuted,
                      margin:"0 0 6px", fontFamily:T.fontSans}}>Right now</p>
                    <p style={{fontSize:13, color:_cpFailed(cpMetabolic)?T.inkMuted:T.inkDark, fontStyle:_cpFailed(cpMetabolic)?"italic":"normal", lineHeight:1.6, margin:"0 0 14px", fontFamily:T.fontSans}}>
                      <AIText text={cpMetabolic} persona={persona}/>
                      {_cpFailed(cpMetabolic)&&<span onClick={onRunCopilot} style={{fontFamily:T.fontSans,fontSize:12,color:T.accent,cursor:"pointer",marginLeft:4,textDecoration:"underline"}}>→ Run audit now</span>}
                    </p>
                    <div style={{height:0.5, background:T.surfaceLight, marginBottom:14}}/>
                  </>
                )}

                {/* RECOVERY */}
                {cpTactical && (
                  <>
                    <p style={{fontSize:10, fontWeight:700, letterSpacing:"1px", color:T.inkMuted,
                      margin:"0 0 6px", fontFamily:T.fontSans}}>Recovery</p>
                    <p style={{fontSize:13, color:_cpFailed(cpTactical)?T.inkMuted:T.inkDark, fontStyle:_cpFailed(cpTactical)?"italic":"normal", lineHeight:1.6, margin:"0 0 14px", fontFamily:T.fontSans}}>
                      <AIText text={cpTactical} persona={persona}/>
                      {_cpFailed(cpTactical)&&<span onClick={onRunCopilot} style={{fontFamily:T.fontSans,fontSize:12,color:T.accent,cursor:"pointer",marginLeft:4,textDecoration:"underline"}}>→ Run audit now</span>}
                    </p>
                    <div style={{height:0.5, background:T.surfaceLight, marginBottom:14}}/>
                  </>
                )}

                {/* OUTLOOK */}
                {cpOutlook && (
                  <>
                    <p style={{fontSize:10, fontWeight:700, letterSpacing:"1px", color:T.inkMuted,
                      margin:"0 0 6px", fontFamily:T.fontSans}}>Outlook</p>
                    <p style={{fontSize:13, color:_cpFailed(cpOutlook)?T.inkMuted:T.inkDark, fontStyle:_cpFailed(cpOutlook)?"italic":"normal", lineHeight:1.6, margin:"0 0 14px", fontFamily:T.fontSans}}>
                      <AIText text={cpOutlook} persona={persona}/>
                      {_cpFailed(cpOutlook)&&<span onClick={onRunCopilot} style={{fontFamily:T.fontSans,fontSize:12,color:T.accent,cursor:"pointer",marginLeft:4,textDecoration:"underline"}}>→ Run audit now</span>}
                    </p>
                  </>
                )}

                {/* ACTION BOX */}
                {cpAction && (
                  <div style={{background:T.surfaceLight, borderRadius:T.radiusSm, padding:14, marginTop:4}}>
                    <p style={{fontSize:10, fontWeight:700, letterSpacing:"1px", color:T.inkMuted,
                      margin:"0 0 6px", fontFamily:T.fontSans}}>{new Date().getHours() < 18 ? "Do this today" : "Do this tomorrow"}</p>
                    <p style={{fontSize:13, color:T.inkDark, lineHeight:1.4, margin:0, fontFamily:T.fontSans, fontWeight:600}}>
                      <AIText text={cpAction} persona={persona}/>
                    </p>
                  </div>
                )}

                <p style={{color:T.inkFaint,fontSize:10,fontFamily:T.fontSans,fontStyle:"italic",textAlign:"center",marginTop:12,lineHeight:1.5,padding:"0 8px"}}>AI analysis is for informational purposes only and does not constitute medical advice.</p>
              </div>
            )}

            {!cpMetabolic&&!cpTactical&&!cpOutlook&&!cpLoading&&(
              <div style={{textAlign:"center",padding:"28px 18px",border:`1px solid ${T.cardBorder}`,
                background:T.card,borderRadius:18}}>
                <Brain size={24} color={T.inkMuted} weight="fill"/>
                <p style={{color:T.inkMuted,fontSize:10,margin:"8px 0 0",
                  fontFamily:T.fontSans,fontWeight:700}}>
                  Log your food, then run your daily audit.
                </p>
              </div>
            )}

            {/* ── V43: Today by Meal (moved from Progress) ── */}
            {mealBreakdown && mealBreakdown.length>0&&(
              <div ref={mealsRef} style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,padding:16,marginBottom:8}}>
                <p style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",margin:"0 0 10px",fontFamily:T.fontSans}}>Today by meal</p>
                {mealBreakdown.map(m=>{
                  const pct=Math.min(100,targets.calories>0?Math.round((m.calories/targets.calories)*100):0);
                  return(
                    <div key={m.label} style={{marginBottom:6}}>
                      <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:2}}>
                        <span style={{fontSize:10,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted,letterSpacing:"0.5px"}}>{m.label}</span>
                        <span style={{fontSize:10,fontFamily:T.fontSans,color:T.inkDark,fontWeight:700}}>{m.calories} kcal <span style={{color:T.inkMuted}}>({pct}%)</span></span>
                      </div>
                      <div style={{height:6,background:T.surfaceLight,borderRadius:3,overflow:"hidden"}}>
                        <div style={{height:"100%",width:pct+"%",background:m.color,transition:"width 0.4s ease",borderRadius:3}}/>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── V43: Meal Distribution (moved from Progress) ── */}
            {(()=>{
              if (!mealBreakdown || mealBreakdown.length === 0) return null;
              const idealPct = Math.round(100 / mealBreakdown.length);
              return (
                <div style={{background:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,padding:16,marginBottom:8}}>
                  <p style={{fontSize:10,fontWeight:700,color:T.inkMuted,letterSpacing:"1px",textTransform:"uppercase",margin:"0 0 6px",fontFamily:T.fontSans}}>Meal distribution</p>
                  <p style={{color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,fontWeight:500,margin:"0 0 8px"}}>Today's calorie split vs even distribution</p>
                  {mealBreakdown.map(m=>{
                    const pct = targets.calories > 0 ? Math.round((m.calories/targets.calories)*100) : 0;
                    const diff = pct - idealPct;
                    return (
                      <div key={m.label} style={{marginBottom:6}}>
                        <div style={{display:"flex",justifyContent:"space-between",alignItems:"baseline",marginBottom:2}}>
                          <span style={{fontSize:10,fontFamily:T.fontSans,fontWeight:700,color:T.inkMuted,letterSpacing:"0.5px"}}>{m.label}</span>
                          <span style={{fontSize:10,fontFamily:T.fontSans,color:diff > 10 ? (T.red||"#ef4444") : diff < -10 ? T.accent : T.inkMuted,fontWeight:700}}>
                            {pct}% of daily {diff > 0 ? `(+${diff}%)` : diff < 0 ? `(${diff}%)` : "(balanced)"}
                          </span>
                        </div>
                        <div style={{height:6,background:T.surfaceLight,borderRadius:3,overflow:"hidden"}}>
                          <div style={{height:"100%",width:Math.min(pct,100)+"%",background:m.color,transition:"width 0.4s ease",borderRadius:3}}/>
                        </div>
                      </div>
                    );
                  })}
                  <p style={{color:T.inkFaint,fontSize:10,fontFamily:T.fontSans,fontWeight:500,margin:"6px 0 0",fontStyle:"italic"}}>
                    Even split target: ~{idealPct}% per meal
                  </p>
                </div>
              );
            })()}

          </div>
        )}

        {/* ============================================================
            V37: UNIFIED WEEKLY VIEW — Brief Me triggers both, Full Picture + Briefing
        ============================================================ */}
        {intelView==="weekly"&&(
          <div style={{padding:"0 16px"}}>
            {/* ── Single Brief Me button triggers both functions ── */}
            <div style={{marginBottom:14}}>
              <button onClick={()=>{ onRunWeeklyBriefing(); onRunCrossApp(); }} disabled={weeklyLoading||crossAppLoading}
                style={{width:"100%",background:T.orange,border:"none",borderRadius:T.radiusSm,padding:14,cursor:weeklyLoading||crossAppLoading?"not-allowed":"pointer",
                  color:"#fff",fontSize:15,fontWeight:800,fontFamily:T.fontDisplay,display:"flex",alignItems:"center",justifyContent:"center",gap:8,opacity:weeklyLoading||crossAppLoading?0.6:1}}>
                {(weeklyLoading||crossAppLoading)
                  ?<><Spinner size={16}/> Generating weekly report…</>
                  :<><Brain size={16}/> Brief me</>}
              </button>
              <p style={{color:T.textDim,fontSize:9,fontFamily:T.fontSans,fontWeight:"bold",textAlign:"center",margin:"6px 0 0"}}>
                {weeklyTs>0&&(wbSummary||crossAppData)
                  ? `Last generated ${Math.floor((Date.now()-weeklyTs)/86400000)} days ago`
                  : "Not yet generated this week"}
              </p>
            </div>

            {/* ── Weekly chat panel ─────────────────────────────────── */}
            <ChatPanel
              title="Ask about this week"
              scopeNote="This week's patterns only · not medical advice"
              placeholder="Ask about this week..."
              cap={weeklyChatCap}
              history={weeklyChatHistory}
              loading={weeklyChatLoading}
              error={weeklyChatError}
              onSend={handleWeeklyChatSend}
            />

            {/* V44: Loading state */}
            {(weeklyLoading||crossAppLoading)&&!wbSummary&&!crossAppData&&(
              <div style={{background:T.card,borderRadius:T.radius,border:T.border,padding:20,textAlign:"center",marginBottom:12}}>
                <Spinner size={20}/>
                <p style={{color:T.textDim,fontSize:13,fontFamily:T.fontSans,fontWeight:"bold",margin:"12px 0 0"}}>Generating your weekly report…</p>
              </div>
            )}

            {/* Empty state — no brief generated yet */}
            {!crossAppData && !wbSummary && wbPros.length===0 && !weeklyLoading && !crossAppLoading && (
              <div style={{
                backgroundColor:T.card,border:`1px solid ${T.cardBorder}`,
                borderRadius:18,padding:"20px 16px",marginTop:12,textAlign:"center",
              }}>
                <div style={{fontFamily:T.fontDisplay,fontSize:16,fontWeight:700,color:T.inkDark,marginBottom:6}}>Your week at a glance</div>
                <div style={{fontFamily:T.fontSans,fontSize:13,color:T.inkMuted,lineHeight:1.5}}>
                  Tap Brief me to generate your weekly nutrition and training summary, including wins, gaps, and next week priorities.
                </div>
              </div>
            )}

            {/* V44: Unified Weekly Report Card — merges Full Picture + Briefing */}
            {(crossAppData || wbSummary || wbPros.length > 0) && (
              <div style={{background:T.card,borderRadius:T.radius,border:`1.5px solid ${T.primary}`,overflow:"hidden",marginBottom:12}}>
                {/* Header */}
                <div style={{display:"flex",alignItems:"center",gap:10,padding:"14px 18px",borderBottom:T.border}}>
                  <div style={{width:32,height:32,borderRadius:8,background:T.primary,display:"flex",alignItems:"center",justifyContent:"center"}}>
                    <span style={{color:"#fff",fontSize:16}}>☀</span>
                  </div>
                  <div>
                    <p style={{fontSize:13,fontWeight:600,color:T.ink,margin:0,fontFamily:T.fontSans}}>This Week's Report</p>
                    <p style={{fontSize:11,color:T.textDim,margin:"1px 0 0",fontFamily:T.fontSans}}>{persona} · 7-day averages · Nutrition + Training</p>
                  </div>
                </div>

                <div style={{padding:18}}>
                  {/* Headline from crossAppData */}
                  {crossAppData?.headline && (
                    <p style={{fontSize:15,fontWeight:600,color:T.primary,lineHeight:1.5,margin:"0 0 16px",fontFamily:T.fontSans}}>
                      <AIText text={crossAppData.headline} persona={persona}/>
                    </p>
                  )}

                  {/* Nutrition / Training / Combined */}
                  {crossAppData?.nutrition_verdict && (
                    <div style={{marginBottom:10}}>
                      <p style={{fontSize:11,fontWeight:700,color:T.primary,margin:"0 0 3px",fontFamily:T.fontSans}}>Nutrition</p>
                      <p style={{fontSize:13,color:T.ink,lineHeight:1.55,margin:0,fontFamily:T.fontSans,fontWeight:"bold"}}><AIText text={crossAppData.nutrition_verdict} persona={persona}/></p>
                    </div>
                  )}
                  {crossAppData?.training_verdict && (
                    <div style={{marginBottom:10}}>
                      <p style={{fontSize:11,fontWeight:700,color:T.primary,margin:"0 0 3px",fontFamily:T.fontSans}}>Training</p>
                      <p style={{fontSize:13,color:T.ink,lineHeight:1.55,margin:0,fontFamily:T.fontSans,fontWeight:"bold"}}><AIText text={crossAppData.training_verdict} persona={persona}/></p>
                    </div>
                  )}
                  {crossAppData?.combined_insight && (
                    <div style={{marginBottom:4}}>
                      <p style={{fontSize:11,fontWeight:700,color:T.primary,margin:"0 0 3px",fontFamily:T.fontSans}}>Combined</p>
                      <p style={{fontSize:13,color:T.ink,lineHeight:1.55,margin:0,fontFamily:T.fontSans,fontWeight:"bold"}}><AIText text={crossAppData.combined_insight} persona={persona}/></p>
                    </div>
                  )}

                  {/* Divider between cross-app and briefing */}
                  {(crossAppData && (wbPros.length>0||wbNegatives.length>0)) && <div style={{height:0.5,background:T.surface,margin:"14px 0"}}/>}

                  {/* Wins */}
                  {wbPros.length > 0 && (
                    <div style={{marginBottom:14}}>
                      <p style={{fontSize:10,fontWeight:700,letterSpacing:"0.8px",color:T.textDim,margin:"0 0 10px",fontFamily:T.fontSans}}>Wins this week</p>
                      {wbPros.map((item, i) => (
                        <div key={i} style={{display:"flex",gap:8,marginBottom:8,alignItems:"flex-start"}}>
                          <span style={{color:T.primary,fontWeight:700,fontSize:13}}>+</span>
                          <p style={{flex:1,fontSize:13,color:T.ink,lineHeight:1.55,margin:0,fontFamily:T.fontSans,fontWeight:"bold"}}><AIText text={item} persona={persona}/></p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Divider */}
                  {wbNegatives.length > 0 && <div style={{height:0.5,background:T.surface,marginBottom:14}}/>}

                  {/* Gaps */}
                  {wbNegatives.length > 0 && (
                    <div style={{marginBottom:14}}>
                      <p style={{fontSize:10,fontWeight:700,letterSpacing:"0.8px",color:T.textDim,margin:"0 0 10px",fontFamily:T.fontSans}}>Gaps to fix</p>
                      {wbNegatives.map((item, i) => (
                        <div key={i} style={{display:"flex",gap:8,marginBottom:8,alignItems:"flex-start"}}>
                          <span style={{color:T.red,fontWeight:700,fontSize:13}}>!</span>
                          <p style={{flex:1,fontSize:13,color:T.ink,lineHeight:1.55,margin:0,fontFamily:T.fontSans,fontWeight:"bold"}}><AIText text={item} persona={persona}/></p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Priorities — action box */}
                  {wbPriorities.length > 0 && (
                    <div style={{background:T.surface,borderRadius:T.radiusSm,padding:14,marginTop:4}}>
                      <p style={{fontSize:10,fontWeight:700,letterSpacing:"0.8px",color:T.textDim,margin:"0 0 10px",fontFamily:T.fontSans}}>Next week priorities</p>
                      {wbPriorities.map((item, i) => (
                        <div key={i} style={{display:"flex",gap:8,marginBottom:8,alignItems:"flex-start"}}>
                          <span style={{color:T.primary,fontWeight:700,fontSize:13,minWidth:16,fontFamily:T.fontSans}}>{i+1}.</span>
                          <p style={{flex:1,fontSize:13,color:T.ink,lineHeight:1.55,margin:0,fontFamily:T.fontSans,fontWeight:"bold"}}><AIText text={item} persona={persona}/></p>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Next Week Focus from crossApp */}
                  {crossAppData?.next_week_focus && !wbPriorities.length && (
                    <div style={{background:T.surface,borderRadius:T.radiusSm,padding:14,marginTop:4}}>
                      <p style={{fontSize:10,fontWeight:700,letterSpacing:"0.8px",color:T.textDim,margin:"0 0 6px",fontFamily:T.fontSans}}>Next week focus</p>
                      <p style={{fontSize:13,color:T.ink,lineHeight:1.55,margin:0,fontFamily:T.fontSans,fontWeight:"bold"}}><AIText text={crossAppData.next_week_focus} persona={persona}/></p>
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div style={{borderTop:T.border,padding:10,textAlign:"center"}}>
                  <p style={{fontSize:11,color:T.textDim,margin:0,fontFamily:T.fontSans}}>
                    {weeklyTs > 0 ? `Last generated ${Math.floor((Date.now() - weeklyTs) / 86400000)} days ago` : "Tap Brief Me to generate"}
                  </p>
                  <p style={{color:T.textDim,fontSize:10,fontFamily:T.fontSans,fontWeight:"bold",marginTop:6,lineHeight:1.5}}>AI analysis is for informational purposes only and does not constitute medical advice.</p>
                </div>
              </div>
            )}

          </div>
        )}

        {/* ============================================================
            CHARTS VIEW — redesigned unified card system
        ============================================================ */}
        {intelView==="charts"&&(
          <div style={{padding:"0 16px",paddingTop:12,paddingBottom:140}}>

            {/* Empty state */}
            {Object.keys(intelByDay).length===0&&!intelLoading&&(
              <div style={{textAlign:"center",padding:"32px 20px",border:`1px solid ${T.cardBorder}`,
                background:T.card,marginBottom:12,borderRadius:18}}>
                <ChartBar size={28} color={T.textDim}/>
                <p style={{color:T.inkDark,fontSize:13,margin:"10px 0 4px",fontFamily:T.fontSans,fontWeight:700}}>
                  No chart data yet
                </p>
                <p style={{color:T.inkMuted,fontSize:11,margin:"0 0 14px",fontFamily:T.fontSans,lineHeight:1.5}}>
                  Log food for at least 2 days, then hit Refresh.
                </p>
                <button onClick={loadIntelMeta}
                  style={{background:T.orange,border:"none",borderRadius:T.radiusSm,
                    padding:"8px 18px",cursor:"pointer",color:"#ffffff",
                    fontSize:11,fontFamily:T.fontSans,fontWeight:700,
                    display:"inline-flex",alignItems:"center",gap:6}}>
                  <ArrowClockwise size={11}/> Refresh
                </button>
              </div>
            )}

            {/* ── CARD 1: Calorie burn-down ── */}
            {(()=>{
              const kcalConsumed = Math.round(totals?.calories || 0);
              const kcalLeft = Math.max(0, (targets.calories||0) - kcalConsumed);
              const overBudget = kcalConsumed > (targets.calories||0);
              return (
                <div style={{backgroundColor:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:12,overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderBottom:"1px solid rgba(255,255,255,0.08)",backgroundColor:T.tileBg}}>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontSize:16}}>🔥</span>
                      <span style={{fontFamily:T.fontSans,fontSize:11,fontWeight:700,color:T.tileAccent,letterSpacing:"0.5px"}}>Calorie burn-down</span>
                    </div>
                    <div style={{display:"flex",alignItems:"center",gap:8}}>
                      <span style={{fontFamily:T.fontSans,fontSize:11,color:T.tileMuted}}>08:00 → 22:00</span>
                      <div style={{backgroundColor:overBudget?`${T.red}20`:T.aiTagBg,borderRadius:T.radiusSm,padding:"2px 8px"}}>
                        <span style={{fontFamily:T.fontSans,fontSize:11,fontWeight:700,color:overBudget?T.red:T.aiTagText}}>
                          {overBudget
                            ? `${kcalConsumed-(targets.calories||0)} kcal over`
                            : `${kcalLeft} kcal left`}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div style={{padding:"12px 12px 8px"}}>
                    {hasTargets
                      ? <BurnDownChart logs={logs} targetCalories={targets.calories}/>
                      : <p style={{color:T.inkMuted,fontSize:11,fontFamily:T.fontSans,fontWeight:600,margin:0,padding:"16px",textAlign:"center"}}>Complete onboarding to activate.</p>
                    }
                  </div>
                </div>
              );
            })()}

            {/* ── CARD 2: Metabolic momentum ── */}
            {(()=>{
              const wr7 = last7iso.map(date=>({
                date, calories: lhByDate[date]?.calories||0,
                target: targets.calories, logged: lhByDate[date]?.logged||false,
              }));
              const daysLogged = wr7.filter(d=>d.calories>0).length;
              const daysMet = wr7.filter(d=>d.calories>0 && d.calories<=(d.target||targets.calories||0)).length;
              const metabolicPct = Math.round((daysMet / 7) * 100);
              const metabolicQuote = daysLogged===0?"Zero data.":daysLogged<4?`${daysLogged}/7 days logged.`:metabolicPct>=85?`${daysMet}/7 days on target.`:metabolicPct>=60?`${daysMet}/7 days. Getting there.`:`${daysMet}/7 days on target. Fix the pattern.`;
              return (
                <div style={{backgroundColor:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:12,overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",padding:"12px 16px",borderBottom:"1px solid rgba(255,255,255,0.08)",backgroundColor:T.tileBg}}>
                    <div style={{width:36,height:36,borderRadius:18,backgroundColor:"rgba(255,255,255,0.12)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginRight:10}}>
                      <span style={{fontSize:16}}>⚡</span>
                    </div>
                    <div>
                      <div style={{fontFamily:T.fontSans,fontSize:11,fontWeight:700,color:T.tileAccent,letterSpacing:"0.5px"}}>Metabolic momentum</div>
                      <div style={{fontFamily:T.fontSans,fontSize:11,color:T.tileMuted}}>Days on-target vs days logged</div>
                    </div>
                  </div>
                  <div style={{padding:16}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
                      <span style={{fontFamily:T.fontDisplay,fontSize:36,fontWeight:800,color:T.orange,minWidth:70,lineHeight:1}}>{metabolicPct}%</span>
                      <div style={{flex:1}}>
                        <div style={{height:8,borderRadius:4,backgroundColor:T.cardBorder,overflow:"hidden"}}>
                          <div style={{height:"100%",borderRadius:4,backgroundColor:T.orange,width:`${metabolicPct}%`,transition:"width 0.4s"}}/>
                        </div>
                        <div style={{fontFamily:T.fontSans,fontSize:11,color:T.inkMuted,marginTop:4}}>{daysMet}/{daysLogged} days hit target</div>
                      </div>
                    </div>
                    <div style={{fontFamily:T.fontSans,fontSize:12,color:T.inkMuted,fontStyle:"italic",paddingTop:8,marginTop:8,borderTop:`1px solid ${T.cardBorder}`}}>"{metabolicQuote}"</div>
                  </div>
                </div>
              );
            })()}

            {/* ── CARD 3: Goal trajectory ── */}
            {(()=>{
              const wr7 = last7iso.map(date=>({
                date, calories: lhByDate[date]?.calories||0,
                target: targets.calories, logged: lhByDate[date]?.logged||false,
              }));
              const daysLogged = wr7.filter(d=>d.calories>0).length;
              const daysMet = wr7.filter(d=>d.calories>0 && d.calories<=(d.target||targets.calories||0)).length;
              const consistencyScore = Math.round((daysLogged / 7) * 40);
              const complianceScore = Math.round((daysMet / 7) * 40);
              const hydScore = 10;
              let trendAdj = 0;
              if (wr7.length >= 3) {
                const f3 = wr7.slice(0,3).reduce((s,d)=>s+d.calories,0)/3;
                const l3 = wr7.slice(-3).reduce((s,d)=>s+d.calories,0)/3;
                if (l3 - f3 > 150) trendAdj = -8;
                if (f3 - l3 > 100) trendAdj = 6;
              }
              const survival = clamp(consistencyScore + complianceScore + hydScore + trendAdj, 4, 96);
              const survivalVerdict = daysLogged===0?"No data.":survival>=80?"On track.":survival>=65?"Mostly on track.":survival>=45?"Off pace.":"Well off target.";
              const factorsStr = `Consistency ${consistencyScore}/40 · compliance ${complianceScore}/40 · hydration ${hydScore}/10 · trend ${trendAdj>0?"+":""}${trendAdj}`;
              return (
                <div style={{backgroundColor:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:12,overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",padding:"12px 16px",borderBottom:"1px solid rgba(255,255,255,0.08)",backgroundColor:T.tileBg}}>
                    <div style={{width:36,height:36,borderRadius:18,backgroundColor:"rgba(255,255,255,0.12)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0,marginRight:10}}>
                      <span style={{fontSize:16}}>🎯</span>
                    </div>
                    <div>
                      <div style={{fontFamily:T.fontSans,fontSize:11,fontWeight:700,color:T.tileAccent,letterSpacing:"0.5px"}}>Goal trajectory</div>
                      <div style={{fontFamily:T.fontSans,fontSize:11,color:T.tileMuted}}>30-day outlook · compliance + trend score</div>
                    </div>
                  </div>
                  <div style={{padding:16}}>
                    <div style={{display:"flex",alignItems:"center",gap:12,marginBottom:8}}>
                      <span style={{fontFamily:T.fontDisplay,fontSize:36,fontWeight:800,color:T.nutriGreen,minWidth:70,lineHeight:1}}>{daysLogged===0?"—":`${survival}%`}</span>
                      {daysLogged>0&&(
                        <div style={{flex:1}}>
                          <div style={{height:8,borderRadius:4,backgroundColor:T.cardBorder,overflow:"hidden"}}>
                            <div style={{height:"100%",borderRadius:4,backgroundColor:T.nutriGreen,width:`${survival}%`,transition:"width 0.4s"}}/>
                          </div>
                          <div style={{fontFamily:T.fontSans,fontSize:11,color:T.inkMuted,marginTop:8,paddingTop:8,borderTop:`1px solid ${T.cardBorder}`}}>{factorsStr}</div>
                        </div>
                      )}
                    </div>
                    <div style={{fontFamily:T.fontSans,fontSize:12,color:T.inkMuted,fontStyle:"italic",paddingTop:8,borderTop:`1px solid ${T.cardBorder}`}}>{daysLogged===0?"Save at least one day.":`"${survivalVerdict}"`}</div>
                  </div>
                </div>
              );
            })()}

            {/* ── CARD 4: Habit consistency ── */}
            {(()=>{
              const METRIC_OPTIONS = ["Calories","Protein","Fiber","Water","Fats","Carbs","Sugar","Sodium"];
              const waterByDay = {};
              (logHistory||[]).forEach(d=>{ if(d.water>0) waterByDay[d.date]=d.water; });
              if ((waterMl||0) > 0) waterByDay[TODAY_ISO()] = waterMl;
              function metricHit(metric, b, dayDate) {
                if (!b?.logged && metric !== "Water") return null;
                const t = targets;
                switch(metric) {
                  case "Calories": { if (!b?.logged) return null; const ratio = b.calories / (t.calories||1); return ratio<=1.05?true:ratio<=1.10?"warn":false; }
                  case "Protein": { if (!b?.logged) return null; const ratio = b.protein / (t.protein||1); return ratio>=0.95?true:ratio>=0.90?"warn":false; }
                  case "Fiber": { if (!b?.logged) return null; const ratio = b.fibre / (t.fibre||25); return ratio>=0.95?true:ratio>=0.85?"warn":false; }
                  case "Water": { const w = waterByDay[dayDate]||0; if (w===0) return null; const ratio = w / (hydrationTarget||1); return ratio>=0.95?true:ratio>=0.90?"warn":false; }
                  case "Fats": { if (!b?.logged) return null; const ratio = b.fat / (t.fat||1); return ratio<=1.05?true:ratio<=1.10?"warn":false; }
                  case "Carbs": { if (!b?.logged) return null; const ratio = b.carbs / (t.carbs||1); return ratio<=1.05?true:ratio<=1.10?"warn":false; }
                  default: return null;
                }
              }
              const habitDates = Array.from({length:7},(_,i)=>{ const d=new Date(); d.setDate(d.getDate()-i); return d.toISOString().slice(0,10); }).reverse();
              const dayLabels = habitDates.map(d=>new Date(d+"T00:00:00").toLocaleDateString("en-GB",{weekday:"short"}).slice(0,2));
              let totalCells=0, redCells=0;
              (habitMetrics||[]).forEach(metric=>{ habitDates.forEach(d=>{ const hit = metricHit(metric, (intelByDay||{})[d], d); if (hit !== null) { totalCells++; if (hit === false) redCells++; } }); });
              const redRatio = totalCells > 0 ? redCells / totalCells : 0;
              return (
                <div style={{backgroundColor:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:12,overflow:"hidden"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",padding:"12px 16px",borderBottom:"1px solid rgba(255,255,255,0.08)",backgroundColor:T.tileBg}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <div style={{width:36,height:36,borderRadius:18,backgroundColor:"rgba(255,255,255,0.12)",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <span style={{fontSize:16}}>📊</span>
                      </div>
                      <div>
                        <div style={{fontFamily:T.fontSans,fontSize:11,fontWeight:700,color:T.tileAccent,letterSpacing:"0.5px"}}>Habit consistency</div>
                        <div style={{fontFamily:T.fontSans,fontSize:11,color:T.tileMuted}}>7-day · up to 5 metrics</div>
                      </div>
                    </div>
                  </div>
                  <div style={{padding:16}}>
                    <div style={{display:"flex",flexWrap:"wrap",gap:5,marginBottom:8}}>
                      {METRIC_OPTIONS.map(m=>{
                        const active = (habitMetrics||[]).includes(m);
                        return (
                          <button key={m} onClick={()=>{ if(setHabitMetrics) setHabitMetrics(prev=>active?prev.filter(x=>x!==m):prev.length<5?[...prev,m]:prev); }} style={{
                            padding:"4px 10px",fontSize:11,fontFamily:T.fontSans,fontWeight:active?700:500,cursor:"pointer",borderRadius:20,
                            backgroundColor:active?T.tileBg:"transparent",
                            color:active?T.tileAccent:T.inkMuted,
                            border:active?`1px solid ${T.tileBg}`:`1px solid ${T.cardBorder}`,
                            opacity:(!active&&(habitMetrics||[]).length>=5)?0.4:1,
                          }}>{m}</button>
                        );
                      })}
                    </div>
                    <div style={{fontFamily:T.fontSans,fontSize:11,color:T.inkFaint,marginBottom:4}}>{(habitMetrics||[]).length}/5 active.</div>
                    <div style={{fontFamily:T.fontSans,fontSize:10,color:T.inkFaint,marginTop:4,marginBottom:10}}>✓ on target · ✗ missed · — no data</div>
                    <div style={{display:"grid",gridTemplateColumns:"80px repeat(7, 1fr)",gap:2}}>
                      <div/>{dayLabels.map((lbl,i)=>(<div key={i} style={{textAlign:"center",color:T.inkMuted,fontSize:10,fontFamily:T.fontSans,fontWeight:600}}>{lbl}</div>))}
                      {(habitMetrics||[]).map(metric=>(<React.Fragment key={metric}>
                        <div style={{color:T.inkDark,fontSize:10,fontFamily:T.fontSans,fontWeight:600,display:"flex",alignItems:"center"}}>{metric}</div>
                        {habitDates.map(d=>{
                          const hit = metricHit(metric, (intelByDay||{})[d], d);
                          const bg = hit===null?T.surfaceLight:hit===true?`${T.primary}22`:hit==="warn"?`${T.accent}22`:`${T.red}22`;
                          const bdr = hit===null?`1px solid ${T.cardBorder}`:hit===true?`2px solid ${T.primary}44`:hit==="warn"?`2px solid ${T.accent}44`:`2px solid ${T.red}44`;
                          return (<div key={d} style={{height:24,background:bg,border:bdr,display:"flex",alignItems:"center",justifyContent:"center"}}>
                            {hit===true&&<span style={{color:T.primary,fontSize:10,fontWeight:800}}>✓</span>}
                            {hit==="warn"&&<span style={{color:T.accent,fontSize:10,fontWeight:800}}>~</span>}
                            {hit===false&&<span style={{color:T.red,fontSize:10,fontWeight:800}}>✗</span>}
                            {hit===null&&<span style={{color:T.inkFaint,fontSize:9}}>–</span>}
                          </div>);
                        })}
                      </React.Fragment>))}
                    </div>
                    {redRatio >= 0.5 && totalCells >= 7 && (
                      <div style={{marginTop:10,backgroundColor:"rgba(239,68,68,0.08)",border:"1px solid rgba(239,68,68,0.2)",borderRadius:T.radiusSm,padding:"8px 12px"}}>
                        <span style={{color:T.red,fontFamily:T.fontSans,fontSize:12,fontWeight:600}}>
                          {redRatio >= 0.7?"More red than green. Fix it.":"Consistency needs work."}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* ── CARD 5: Water intake table ── */}
            {(()=>{
              const waterDays = last7iso.map(date => {
                const entry = (logHistory||[]).find(d => d.date === date);
                return { date, water: entry?.water || 0 };
              });
              const hasWater = waterDays.some(d => d.water > 0);
              if (!hasWater) return null;
              const wTarget = hydrationTarget || 2500;
              const daysWithData = waterDays.filter(d => d.water > 0);
              const avgWater = daysWithData.length > 0
                ? (daysWithData.reduce((s,d)=>s+d.water,0) / daysWithData.length / 1000).toFixed(1)
                : "0.0";
              return (
                <div style={{backgroundColor:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:12,overflow:"hidden"}}>
                  <div style={{backgroundColor:T.tileBg,borderRadius:"18px 18px 0 0",padding:"12px 16px",display:"flex",alignItems:"center",justifyContent:"space-between",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                    <div style={{display:"flex",alignItems:"center",gap:10}}>
                      <span style={{fontSize:18}}>💧</span>
                      <div>
                        <div style={{fontFamily:T.fontDisplay,fontSize:15,fontWeight:700,color:T.tileAccent,lineHeight:1.1}}>Water intake</div>
                        <div style={{fontFamily:T.fontSans,fontSize:12,color:"rgba(255,255,255,0.5)"}}>7-day · vs target</div>
                      </div>
                    </div>
                    <span style={{fontFamily:T.fontSans,fontSize:12,fontWeight:600,color:"rgba(255,255,255,0.6)"}}>Target: {(wTarget/1000).toFixed(1)}L</span>
                  </div>
                  {[...waterDays].reverse().map((d, i) => {
                    const pct = wTarget > 0 ? Math.min(100, Math.round((d.water/wTarget)*100)) : 0;
                    const isToday = d.date === TODAY_ISO();
                    const hitTarget = d.water >= wTarget;
                    const hasData = d.water > 0;
                    const barColor = hitTarget ? T.nutriGreen : T.hydrationBlue;
                    const label = new Date(d.date+"T00:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"});
                    return (
                      <div key={d.date} style={{
                        display:"flex",alignItems:"center",padding:"10px 16px",
                        backgroundColor:isToday?T.accentLight:i%2===0?T.card:T.surfaceLight,
                        borderBottom:i<6?`1px solid ${T.cardBorder}`:"none",
                      }}>
                        <div style={{width:90,fontFamily:T.fontSans,fontSize:12,color:isToday?T.inkDark:T.inkMuted,fontWeight:isToday?700:400,flexShrink:0}}>
                          {isToday?"Today":label}
                        </div>
                        <div style={{flex:1,height:8,borderRadius:4,backgroundColor:T.cardBorder,overflow:"hidden",margin:"0 12px"}}>
                          <div style={{height:"100%",borderRadius:4,backgroundColor:hasData?barColor:"transparent",width:`${pct}%`}}/>
                        </div>
                        <div style={{width:48,textAlign:"right",fontFamily:T.fontSans,fontSize:14,fontWeight:700,color:hitTarget?T.nutriGreen:hasData?T.inkDark:T.inkFaint,flexShrink:0}}>
                          {hasData?(d.water/1000).toFixed(1)+"L":"—"}
                        </div>
                        {hitTarget&&<div style={{marginLeft:8,fontSize:14,flexShrink:0,color:T.nutriGreen}}>✓</div>}
                      </div>
                    );
                  })}
                  <div style={{padding:"10px 16px",borderTop:`1px solid ${T.cardBorder}`,backgroundColor:T.surfaceMid,display:"flex",justifyContent:"space-between",alignItems:"center"}}>
                    <span style={{fontFamily:T.fontSans,fontSize:11,color:T.inkMuted}}>7-day average</span>
                    <span style={{fontFamily:T.fontDisplay,fontSize:13,fontWeight:700,color:T.inkDark}}>{avgWater}L/day</span>
                  </div>
                </div>
              );
            })()}

            {/* ── CARD 6: 7-day log table ── */}
            {(()=>{
              const getVsColor = (actual, target) => {
                if (!target || target === 0) return T.inkMuted;
                const pct = Math.abs((actual - target) / target) * 100;
                if (pct <= 5)  return T.nutriGreen;
                if (pct <= 10) return T.orange;
                return T.red;
              };
              const getDeltaText = (actual, target, unit) => {
                if (actual === null || actual === undefined) return { text:"—", color:T.inkFaint };
                if (!target || target === 0) return { text:`${Math.round(actual)}${unit}`, color:T.inkDark };
                const delta = actual - target;
                const color = getVsColor(actual, target);
                const sign = delta > 0 ? "+" : "";
                const text = Math.abs(delta) < 1 ? "✓" : `${sign}${Math.round(delta)}${unit}`;
                return { text, color };
              };
              const METRIC_CONFIG = {
                Calories: { target: targets.calories,       unit: "kcal", label: "Cal"  },
                Protein:  { target: targets.protein,        unit: "g",    label: "P"    },
                Carbs:    { target: targets.carbs,          unit: "g",    label: "C"    },
                Fat:      { target: targets.fat,            unit: "g",    label: "F"    },
                Fibre:    { target: targets.fibre||30,      unit: "g",    label: "Fi"   },
                Water:    { target: hydrationTarget||2500,  unit: "ml",   label: "H₂O"  },
                Sugar:    { target: null,                   unit: "g",    label: "Sug"  },
                Sodium:   { target: null,                   unit: "mg",   label: "Na"   },
              };
              // Enforce canonical column order
              const METRIC_ORDER = ["Calories","Protein","Carbs","Fat","Fibre","Water","Sugar","Sodium"];
              const orderedMetrics = [...logMetrics].sort((a,b) => METRIC_ORDER.indexOf(a) - METRIC_ORDER.indexOf(b));
              const weekData = [...last7iso].reverse().map(date => {
                const entry = lhByDate[date];
                const logged = entry?.logged || false;
                return {
                  date,
                  dateLabel: new Date(date+"T00:00:00").toLocaleDateString("en-GB",{weekday:"short",day:"numeric",month:"short"}),
                  isToday: date === TODAY_ISO(),
                  calories: logged ? (entry.calories || 0) : null,
                  protein:  logged ? (entry.protein  || 0) : null,
                  carbs:    logged ? (entry.carbs    || 0) : null,
                  fat:      logged ? (entry.fat      || 0) : null,
                  fibre:    logged ? (entry.fibre    || 0) : null,
                  water:    entry  ? (entry.water    || 0) : null,
                  sugar:    logged ? (entry.sugar    || 0) : null,
                  sodium:   logged ? (entry.sodium   || 0) : null,
                };
              });
              const hasAnyData = weekData.some(d => d.calories !== null || (d.water !== null && d.water > 0));
              return (
                <div style={{backgroundColor:T.card,border:`1px solid ${T.cardBorder}`,borderRadius:18,marginBottom:0,overflow:"hidden"}}>
                  {/* Card header */}
                  <div style={{backgroundColor:T.tileBg,borderRadius:"18px 18px 0 0",padding:"12px 16px",display:"flex",alignItems:"center",borderBottom:"1px solid rgba(255,255,255,0.08)"}}>
                    <span style={{fontSize:18,marginRight:10}}>📋</span>
                    <div>
                      <div style={{fontFamily:T.fontDisplay,fontSize:15,fontWeight:700,color:T.tileAccent,lineHeight:1.1}}>7-day log</div>
                      <div style={{fontFamily:T.fontSans,fontSize:12,color:"rgba(255,255,255,0.5)"}}>Macro vs target · last 7 days</div>
                    </div>
                  </div>
                  {/* Filter pills */}
                  <div style={{padding:"12px 16px 4px",display:"flex",flexWrap:"wrap",gap:4}}>
                    {LOG_METRICS.map(metric=>{
                      const active=logMetrics.includes(metric);
                      return (
                        <div key={metric} onClick={()=>toggleLogMetric(metric)} style={{
                          backgroundColor:active?T.surfaceMid:"transparent",
                          color:active?T.inkDark:T.inkFaint,
                          border:active?`1px solid ${T.accent}`:`1px solid ${T.cardBorder}`,
                          borderRadius:20,padding:"5px 12px",
                          fontFamily:T.fontSans,fontSize:12,fontWeight:active?700:400,
                          cursor:"pointer",userSelect:"none",
                        }}>{metric}</div>
                      );
                    })}
                  </div>
                  <div style={{fontFamily:T.fontSans,fontSize:11,color:T.inkFaint,padding:"4px 16px 10px"}}>
                    {logMetrics.length}/8 selected.
                  </div>
                  {/* Table */}
                  <div style={{borderTop:`1px solid ${T.cardBorder}`}}>
                    <div style={{display:"flex",backgroundColor:T.surfaceMid,padding:"8px 12px",borderBottom:`1px solid ${T.cardBorder}`}}>
                      <div style={{flex:1.5,fontFamily:T.fontSans,fontSize:11,fontWeight:600,color:T.inkMuted}}>Date</div>
                      {orderedMetrics.map(m=>(
                        <div key={m} style={{flex:1,fontFamily:T.fontSans,fontSize:11,fontWeight:600,color:T.inkMuted,textAlign:"center"}}>
                          {METRIC_CONFIG[m].label}
                        </div>
                      ))}
                    </div>
                    {!hasAnyData?(
                      <div style={{padding:"22px 15px",textAlign:"center"}}>
                        <p style={{color:T.inkMuted,fontSize:12,fontFamily:T.fontSans,fontWeight:600,margin:0}}>No history yet. Log food to begin.</p>
                      </div>
                    ):(
                      weekData.map((day,i)=>(
                        <div key={day.date} style={{
                          display:"flex",alignItems:"center",padding:"10px 12px",
                          backgroundColor:i%2===0?T.card:T.surfaceLight,
                          borderBottom:i<weekData.length-1?`1px solid ${T.cardBorder}`:"none",
                        }}>
                          <div style={{flex:1.5,fontFamily:T.fontSans,fontSize:12,color:day.isToday?T.inkDark:T.inkMuted,fontWeight:day.isToday?700:400,fontStyle:day.isToday?"italic":"normal"}}>
                            {day.isToday?"Today":day.dateLabel}
                          </div>
                          {orderedMetrics.map(metric=>{
                            const cfg=METRIC_CONFIG[metric];
                            const logged=day[metric.toLowerCase()];
                            const {text,color}=getDeltaText(logged,cfg.target,cfg.unit);
                            return(
                              <div key={metric} style={{flex:1,textAlign:"center",fontFamily:T.fontSans,fontSize:13,fontWeight:600,color}}>{text}</div>
                            );
                          })}
                        </div>
                      ))
                    )}
                  </div>
                </div>
              );
            })()}

          </div>
        )}

        <div style={{height:40}}/>
        </div>{/* end shared scroll container */}
      </div>

      <BottomNav screen={screen} setScreen={setScreen} hasTargets={hasTargets}/>
    </div></div>
  );
}
